<template>
  <div>
  <nav v-if="showBoothNav" class="aiq-booth-nav" aria-label="Booth toolkit">
    <span class="aiq-booth-nav__label">Booth {{ boothNumber }}</span>
    <a :href="`/booth-app.html?booth=${boothNumber}`">Menu</a>
    <a :href="`/login?booth=${boothNumber}`" class="is-active" aria-current="page">Visitor login</a>
    <a :href="`/booth-staff.html?booth=${boothNumber}`">Live sign-ups</a>
    <a :href="`/visit.html?booth=${boothNumber}`">Sign-up form</a>
    <a :href="`/accessiq-qr.html?booth=${boothNumber}`">QR sign</a>
    <a href="/login">Admin</a>
  </nav>
  <div class="auth-container" :style="containerStyle">
    <div class="auth-box card">
      <!-- Dynamic Logo -->
      <div class="login-logo" v-if="brandingLogo && !logoError">
        <img :src="brandingLogo" :alt="organizationName" @error="handleLogoError" />
      </div>
      <div v-else-if="logoError" class="login-logo-text">
        <h1>{{ organizationName }}</h1>
      </div>
      <h2 class="text-center">{{ loginTitle }}</h2>
      <!-- OAuth Login Options -->
      <div class="oauth-section">
        <div class="oauth-buttons">
          <button @click="loginWithGoogle" class="oauth-btn google-btn" :disabled="oauthLoading">
            <i class="fab fa-google"></i>
            <span>Continue with Google</span>
          </button>
          <button @click="loginWithMicrosoft" class="oauth-btn microsoft-btn" :disabled="oauthLoading">
            <i class="fab fa-microsoft"></i>
            <span>Continue with Microsoft</span>
          </button>
          <button @click="loginWithLinkedIn" class="oauth-btn linkedin-btn" :disabled="oauthLoading">
            <i class="fab fa-linkedin"></i>
            <span>Continue with LinkedIn</span>
          </button>
          <!-- Apple Sign-In - Commented out until Apple Developer account is ready
          <button @click="loginWithApple" class="oauth-btn apple-btn" :disabled="oauthLoading">
            <i class="fab fa-apple"></i>
            <span>Continue with Apple</span>
          </button>
          -->
        </div>
        
        <div class="divider">
          <span>or continue with email</span>
        </div>
      </div>

      <!-- Traditional Email/Password Login -->
      <form @submit.prevent="handleLogin">
        <div class="form-group">
          <label for="username">Email Address</label>
          <input type="text" id="username" v-model="username" class="form-control" required autocomplete="username">
        </div>
        <div class="form-group">
          <label for="password">Password</label>
          <div class="password-input-wrapper">
            <input :type="showPassword ? 'text' : 'password'" id="password" v-model="password" class="form-control" required autocomplete="current-password">
            <button type="button" class="password-toggle-btn" @click="showPassword = !showPassword" :aria-label="showPassword ? 'Hide password' : 'Show password'" tabindex="-1">
              <i :class="showPassword ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
            </button>
          </div>
        </div>
        <div class="forgot-password-link">
          <router-link to="/forgot-password">Forgot your password?</router-link>
        </div>
        <div v-if="error" :class="errorIsOutage ? 'outage-message' : 'error-message'">{{ error }}</div>
        <button type="submit" class="btn btn-block" :disabled="loading">
          <span v-if="loading" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
          {{ loading ? 'Logging in...' : 'Login' }}
        </button>
      </form>
      
      <!-- Organization-specific registration and links -->
      <div v-if="showSCAVLinks" class="scav-links-section">
        <div class="scav-link-item">
          <span>Not a member?</span>
          <a href="https://scav.memberclicks.net/index.php?option=com_mcform&view=ngforms&id=2056696#/" target="_blank" class="scav-link">Join SCAV</a>
        </div>
        <div class="scav-link-item">
          <span>Don't have an account?</span>
          <router-link to="/register" class="scav-link"><strong>Create Account! NOW</strong></router-link>
        </div>
      </div>
    </div>
  </div>
  </div>
</template>

<script>
import auth from '@/services/auth';
import whitelabelBranding from '@/services/whitelabelBrandingService';
import { resolveClientLandingPage, isForbiddenComplianceLanding } from '@/utils/landingPage';
import { safeAppReturnPath } from '@/utils/safeAppReturnPath';
import {
  authErrorFromOutage,
  runWithOutageRetry,
} from '@/services/backendHealth';

export default {
  name: 'LoginView',
  data() {
    return {
      username: '',
      password: '',
      showPassword: false,
      loading: false,
      oauthLoading: false,
      error: '',
      errorIsOutage: false,
      organizationSlug: null,
      branding: null,
      logoError: false,
    };
  },

  computed: {
    boothNumber() {
      const q = this.$route.query.booth;
      if (q) return String(q).slice(0, 32);
      try {
        return sessionStorage.getItem('accessiq_booth') || '304';
      } catch {
        return '304';
      }
    },
    showBoothNav() {
      return !!this.$route.query.booth;
    },
    brandingLogo() {
      return whitelabelBranding.getLogo();
    },
    
    organizationName() {
      return whitelabelBranding.getAppName();
    },
    
    loginTitle() {
      if (this.branding && this.branding.app_name) {
        return `Sign in to ${this.branding.app_name}`;
      }
      return 'Login';
    },
    
    containerStyle() {
      const primaryColor = whitelabelBranding.getColor('primary');
      return {
        '--login-primary': primaryColor,
      };
    },
    
    showSCAVLinks() {
      // Show SCAV-specific links for Colibri Media Group / Vet Media
      const host = window.location.hostname.toLowerCase();
      const isVetMediaDomain = host.includes('vetmediagroup.com') || host.includes('scav.org');
      const isSCAVOrg = this.organizationSlug === 'colibri-media-group';
      
      return isVetMediaDomain || isSCAVOrg;
    },
  },
  
  async mounted() {
    // Booth staff: clear any platform-admin session before visitor logs in
    if (this.$route.query.booth && auth.isAuthenticated()) {
      auth.logout();
    }
    if (this.$route.query.booth) {
      try {
        sessionStorage.setItem('accessiq_booth', String(this.$route.query.booth).slice(0, 32));
      } catch { /* ignore */ }
    }

    // Get organization from URL parameter or detect from domain
    this.organizationSlug = this.$route.query.org;
    
    // If no org parameter, try to detect from domain
    if (!this.organizationSlug) {
      const host = window.location.hostname.toLowerCase();
      
      // Map domains to organizations
      if (host.includes('vetmediagroup.com') || host.includes('scav.org')) {
        this.organizationSlug = 'colibri-media-group';
        console.log('🌐 Detected SCAV domain:', host);
      }
      // Add more domain mappings here for other organizations
    }
    
    if (this.organizationSlug) {
      try {
        await runWithOutageRetry(() =>
          whitelabelBranding.loadOrganizationBranding(this.organizationSlug)
        );
        this.branding = whitelabelBranding.getCurrentBranding();
        console.log('✅ Loaded branding for', this.organizationSlug);
      } catch (error) {
        // Outage or branding failure — keep defaults; never block login UI
        console.error('⚠️ Failed to load org branding:', error);
      }
    }
  },
  
  methods: {
    handleLogoError() {
      console.warn('Logo failed to load, falling back to text');
      this.logoError = true;
    },
    async handleLogin() {
      this.loading = true;
      this.clearLoginError();
      try {
        await runWithOutageRetry(() =>
          auth.login(this.username, this.password, this.organizationSlug)
        );
        
        // Reload branding after successful login based on user's organization
        await this.reloadBrandingAfterLogin();
        
        // Redirect based on user role and organization — check force-change flag first
        this.postLoginRedirect();
      } catch (error) {
        this.setAuthError(error);
        console.error('Login failed:', error);
      } finally {
        this.loading = false;
      }
    },

    clearLoginError() {
      this.error = '';
      this.errorIsOutage = false;
    },

    setAuthError(error, fallbackMessage = 'An unexpected error occurred. Please try again.') {
      const mapped = authErrorFromOutage(error, fallbackMessage);
      this.errorIsOutage = mapped.isOutage;
      this.error = mapped.message;
    },
    
    async reloadBrandingAfterLogin() {
      try {
        // Get the logged-in user's organization
        const user = auth.getUser();
        if (user && user.organization && user.organization.slug) {
          console.log('🎨 Reloading branding for organization:', user.organization.slug);
          await runWithOutageRetry(() =>
            whitelabelBranding.loadOrganizationBranding(user.organization.slug)
          );
        }
      } catch (error) {
        console.warn('Failed to reload branding after login:', error);
        // Continue - app will use default branding
      }
    },

    getDefaultRoute() {
      return resolveClientLandingPage(auth);
    },

    postLoginRedirect() {
      const user = auth.getUser();
      if (user && user.must_change_password && !user.conference_guest_expires_at) {
        this.$router.push('/change-password');
        return;
      }
      if (sessionStorage.getItem('conference_mode') === '1' || user?.conference_guest_expires_at) {
        this.$router.push('/coverage-risk');
        return;
      }
      const defaultRoute = this.getDefaultRoute();
      const requested = safeAppReturnPath(String(this.$route.query.redirect || ''));
      const dest = (requested && !isForbiddenComplianceLanding(requested))
        ? requested
        : defaultRoute;
      this.$router.push(dest);
    },

    // OAuth Login Methods
    async loginWithGoogle() {
      this.oauthLoading = true;
      this.clearLoginError();
      
      try {
        // Initialize Google OAuth
        if (!window.google) {
          throw new Error('Google OAuth library not loaded');
        }

        // Get Google OAuth token
        const response = await new Promise((resolve, reject) => {
          window.google.accounts.oauth2.initTokenClient({
            client_id: '738579302482-d72d97klrfoo3cdguk4pv1mnbmjb0bjp.apps.googleusercontent.com',
            scope: 'openid email profile',
            callback: (tokenResponse) => {
              if (tokenResponse.access_token) {
                resolve(tokenResponse);
              } else {
                reject(new Error('Failed to get Google access token'));
              }
            },
            error_callback: (error) => {
              reject(new Error(`Google OAuth error: ${error.error}`));
            }
          }).requestAccessToken();
        });

        // Send token to backend (with organization slug for whitelabel assignment)
        await runWithOutageRetry(() =>
          auth.loginWithGoogle(response.access_token, this.organizationSlug)
        );
        
        // Reload branding after successful login based on user's organization
        await this.reloadBrandingAfterLogin();
        
        // Redirect based on user role and organization — check force-change flag first
        this.postLoginRedirect();
        
      } catch (error) {
        this.setAuthError(error, 'Google login failed. Please try again.');
        console.error('Google OAuth failed:', error);
      } finally {
        this.oauthLoading = false;
      }
    },

    async loginWithMicrosoft() {
      this.oauthLoading = true;
      this.clearLoginError();
      
      try {
        // Initialize Microsoft MSAL
        if (!window.msal) {
          // Dynamically import MSAL
          const { PublicClientApplication } = await import('@azure/msal-browser');
          
          const msalConfig = {
            auth: {
              clientId: '3382dd6d-0814-4caa-a5e6-fafd36e48248',
              authority: 'https://login.microsoftonline.com/6c1af1f0-0b5d-43bc-bfd6-721a805f40c9',
              redirectUri: window.location.origin
            }
          };
          
          window.msal = new PublicClientApplication(msalConfig);
        }

        // Login with popup
        const loginRequest = {
          scopes: ['openid', 'profile', 'email', 'User.Read']
        };

        const response = await window.msal.loginPopup(loginRequest);
        
        if (response.accessToken) {
          // Send token to backend (with organization slug for whitelabel assignment)
          await runWithOutageRetry(() =>
            auth.loginWithMicrosoft(response.accessToken, this.organizationSlug)
          );
          
          // Reload branding after successful login based on user's organization
          await this.reloadBrandingAfterLogin();
          
          // Redirect based on user role and organization — check force-change flag first
          this.postLoginRedirect();
        } else {
          throw new Error('Failed to get Microsoft access token');
        }
        
      } catch (error) {
        this.setAuthError(error, 'Microsoft login failed. Please try again.');
        console.error('Microsoft OAuth failed:', error);
      } finally {
        this.oauthLoading = false;
      }
    },

    async loginWithLinkedIn() {
      this.oauthLoading = true;
      this.clearLoginError();
      
      try {
        // LinkedIn OAuth flow with OpenID Connect
        const clientId = '78hn1mgn3qqjz0';
        const redirectUri = encodeURIComponent(`${window.location.origin.replace(':3000', ':8000')}/api/users/auth/linkedin/callback/`);
        const scope = encodeURIComponent('openid profile email');  // OpenID Connect scopes
        const state = Math.random().toString(36).substring(7);
        
        // Store state for validation
        localStorage.setItem('linkedin_oauth_state', state);
        
        // LinkedIn OAuth URL pointing to our backend callback
        const linkedinAuthUrl = `https://www.linkedin.com/oauth/v2/authorization?` +
          `response_type=code&` +
          `client_id=${clientId}&` +
          `redirect_uri=${redirectUri}&` +
          `scope=${scope}&` +
          `state=${state}`;
        
        // Open LinkedIn OAuth in popup
        const popup = window.open(
          linkedinAuthUrl,
          'linkedin-oauth',
          'width=500,height=600,scrollbars=yes,resizable=yes'
        );
        
        // Listen for messages from the popup (sent by our callback page)
        const messageHandler = (event) => {
          // Verify origin for security
          if (event.origin !== window.location.origin.replace(':3000', ':8000')) {
            return;
          }
          
          if (event.data.type === 'LINKEDIN_AUTH_SUCCESS') {
            // Store tokens and user data
            this.setToken(event.data.tokens.access);
            this.setRefreshToken(event.data.tokens.refresh);
            auth.setToken(event.data.tokens.access);
            auth.setRefreshToken(event.data.tokens.refresh);
            auth.setUser(event.data.user);
            
            // Clean up
            window.removeEventListener('message', messageHandler);
            popup.close();
            
            // Redirect based on user role and organization — check force-change flag first
            this.postLoginRedirect();
            this.oauthLoading = false;
            
          } else if (event.data.type === 'LINKEDIN_AUTH_ERROR') {
            window.removeEventListener('message', messageHandler);
            popup.close();
            this.setAuthError(
              { message: event.data.error || 'LinkedIn authentication failed.' },
              'LinkedIn authentication failed.'
            );
            this.oauthLoading = false;
          }
        };
        
        // Add message listener
        window.addEventListener('message', messageHandler);
        
        // Handle popup being closed manually
        const checkClosed = setInterval(() => {
          if (popup.closed) {
            clearInterval(checkClosed);
            window.removeEventListener('message', messageHandler);
            if (this.oauthLoading) {
              this.setAuthError(
                { message: 'LinkedIn authentication was cancelled.' },
                'LinkedIn authentication was cancelled.'
              );
              this.oauthLoading = false;
            }
          }
        }, 1000);
        
      } catch (error) {
        this.setAuthError(error, 'LinkedIn login failed. Please try again.');
        console.error('LinkedIn OAuth failed:', error);
        this.oauthLoading = false;
      }
    },

    async loginWithApple() {
      this.oauthLoading = true;
      this.clearLoginError();
      
      try {
        // This would typically open Apple Sign-In popup/redirect
        // For now, we'll simulate with a placeholder
        this.setAuthError(
          { message: 'Apple Sign-In integration requires client-side setup. Please configure Apple Sign-In credentials.' },
          'Apple Sign-In integration requires client-side setup. Please configure Apple Sign-In credentials.'
        );
      } catch (error) {
        this.setAuthError(error, 'Apple Sign-In failed. Please try again.');
        console.error('Apple OAuth failed:', error);
      } finally {
        this.oauthLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.auth-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background-color: var(--color-surface);
}
.auth-box {
  width: 100%;
  max-width: 400px;
}

.outage-message {
  background-color: #e0efff;
  border: 1px solid #d9e2ef;
  color: #212938;
  padding: 0.75rem 1rem;
  border-radius: 6px;
  margin-bottom: 1rem;
  font-size: 0.95rem;
  text-align: center;
}
.text-center {
  text-align: center;
}
.mt-3 {
  margin-top: var(--spacing-md);
}
label {
  display: block;
  margin-bottom: var(--spacing-sm);
  font-weight: 600;
}

/* OAuth Styling */
.oauth-section {
  margin-bottom: 2rem;
}

.oauth-buttons {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
}

.oauth-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 0.875rem 1rem;
  border: 1px solid #e1e5e9;
  border-radius: var(--border-radius);
  background: white;
  color: #333;
  font-weight: 500;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.2s ease;
  text-decoration: none;
}

.oauth-btn:hover:not(:disabled) {
  background: #f8f9fa;
  border-color: #d1d5db;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.oauth-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.oauth-btn i {
  font-size: 1.1rem;
}

/* Whitelabel Logo Styling */
.login-logo {
  text-align: center;
  margin-bottom: 2rem;
}

.login-logo img {
  max-width: 250px;
  max-height: 80px;
  object-fit: contain;
}

.login-logo-text {
  text-align: center;
  margin-bottom: 2rem;
}

.login-logo-text h1 {
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--login-primary, var(--color-primary, #1E40AF));
  margin: 0;
}

/* Use custom primary color from branding */
.btn-primary,
.btn-block {
  background-color: var(--login-primary, var(--color-primary, #1E40AF));
}

.btn-primary:hover,
.btn-block:hover {
  background-color: var(--login-primary, var(--color-primary, #1E40AF));
  filter: brightness(110%);
}

/* Provider-specific colors */
.google-btn:hover:not(:disabled) {
  border-color: #4285f4;
  color: #4285f4;
}

.microsoft-btn:hover:not(:disabled) {
  border-color: #0078d4;
  color: #0078d4;
}

.linkedin-btn:hover:not(:disabled) {
  border-color: #0a66c2;
  color: #0a66c2;
}

.apple-btn:hover:not(:disabled) {
  border-color: #000;
  color: #000;
}

.divider {
  position: relative;
  text-align: center;
  margin: 1.5rem 0;
}

.divider::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  height: 1px;
  background: #e1e5e9;
}

.divider span {
  background: white;
  padding: 0 1rem;
  color: #6b7280;
  font-size: 0.875rem;
}

/* Password Visibility Toggle */
.password-input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.password-input-wrapper .form-control {
  padding-right: 2.75rem;
  width: 100%;
}

.password-toggle-btn {
  position: absolute;
  right: 0.75rem;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: #9ca3af;
  line-height: 1;
  display: flex;
  align-items: center;
  transition: color 0.2s ease;
}

.password-toggle-btn:hover {
  color: #4b5563;
}

.password-toggle-btn i {
  font-size: 0.95rem;
}

/* Forgot Password Link */
.forgot-password-link {
  text-align: right;
  margin-bottom: 1rem;
  margin-top: -0.5rem;
}

.forgot-password-link a {
  color: #6b7280;
  text-decoration: none;
  font-size: 0.875rem;
  transition: color 0.2s ease;
}

.forgot-password-link a:hover {
  color: var(--login-primary, var(--color-primary, #1E40AF));
  text-decoration: underline;
}

/* SCAV-specific links section */
.scav-links-section {
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid #e1e5e9;
  text-align: center;
}

.scav-link-item {
  margin: 0.75rem 0;
  font-size: 0.95rem;
  color: #6b7280;
}

.scav-link-item span {
  margin-right: 0.5rem;
}

.scav-link {
  color: #5b7ce6;
  text-decoration: none;
  font-weight: 500;
  transition: color 0.2s ease;
}

.scav-link:hover {
  color: #4a63c4;
  text-decoration: underline;
}

.scav-link-item a[target="_blank"]::after {
  content: " ↗";
  font-size: 0.8em;
  opacity: 0.7;
}
</style>

<style>
/* Booth toolkit nav on visitor login (?booth=) — full-width, not scoped */
.aiq-booth-nav {
  position: sticky;
  top: 0;
  z-index: 9999;
  background: #212938;
  border-bottom: 2px solid #008ca6;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
}
.aiq-booth-nav__label {
  color: #b0bac9;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  margin-right: 4px;
  white-space: nowrap;
}
.aiq-booth-nav a {
  display: inline-block;
  padding: 6px 10px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  color: #ffffff;
  text-decoration: none;
  background: rgba(255, 255, 255, 0.08);
  white-space: nowrap;
}
.aiq-booth-nav a:hover,
.aiq-booth-nav a.is-active {
  background: #0059b2;
}
</style> 